import { nextDayStart } from "@squire/core";

/** Never reload twice in a row: the page must have lived this long first. */
const MIN_UPTIME = 10 * 60_000;
const POSTPONE = 5 * 60_000;

/**
 * Daily soft restart at 04:00 local time (SPEC 8): reloads the client as a safety net
 * against leaks we did not catch. State lives in the room (server or localStorage), so nothing is lost.
 * Checks once a minute instead of one long timer, which drifts on sleeping devices.
 */
export class SoftRestart {
  private timer: ReturnType<typeof setInterval> | null = null;
  private target = 0;
  private readonly startedAt: number;

  constructor(
    private readonly tz: () => string,
    private readonly busy: () => boolean,
    private readonly clock: () => number = () => Date.now(),
    private readonly reload: () => void = () => location.reload(),
  ) {
    this.startedAt = clock();
  }

  start(): void {
    if (this.timer) return;
    this.target = nextDayStart(this.clock(), this.tz());
    this.timer = setInterval(() => this.check(), 60_000);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Returns true when it reloads. */
  check(): boolean {
    const now = this.clock();
    if (now < this.target) return false;
    if (now - this.startedAt < MIN_UPTIME || this.busy()) {
      this.target = now + POSTPONE;
      return false;
    }
    this.stop();
    this.reload();
    return true;
  }
}
